import React, { useState, useEffect, useMemo } from 'react';
import QRCode from 'qrcode';
import { QrCode, X, Phone, MessageCircle, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import { buildVcard } from '@/lib/vcard';

/**
 * SaveMyDetailsWidget — floating bottom-right pill → modal with dual QR.
 *
 * Renders ONLY for:
 *   - TurboCharge-tier doctors (doctor.tier === 'turbo'), OR
 *   - The sample/demo doctor (always on, so the page functions as showcase).
 *
 * Hidden on narrow screens where scanning your own page is pointless.
 */
const DEMO_DOCTOR_SLUGS = ['dr-rajesh-sharma'];

export default function SaveMyDetailsWidget({ doctor, slug }) {
  const [open, setOpen] = useState(false);
  const [messageQr, setMessageQr] = useState('');
  const [vcardQr, setVcardQr] = useState('');

  const isDemo = DEMO_DOCTOR_SLUGS.includes(slug);
  const isEnabled = isDemo || doctor?.tier === 'turbo';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // The vCard's `URL:` field points to the "/hi" landing page — when the
  // patient taps the saved contact's website field later, they land on
  // the conversion CTA (WhatsApp deep-link or in-app chat) rather than
  // the full profile. Same page is also the destination of the right-side
  // "Message me now" QR below.
  const landingUrl = useMemo(() => `${origin}/dr/${slug}/hi`, [origin, slug]);

  // Build the "Message me now" QR target. If the doctor explicitly opted
  // in to share their phone in the QR, encode a `wa.me/91...` deep-link
  // with a pre-filled message — the patient gets WhatsApp open with the
  // doctor's chat already loaded. Otherwise we route through the landing
  // page which deep-links into the in-app chat (doctor's number stays
  // private).
  const phoneE164 = (doctor?.phone || '').replace(/\D/g, '').slice(-10);
  const canWhatsApp = !!doctor?.qr_share_phone && phoneE164.length === 10;
  const messageQrUrl = useMemo(() => {
    if (canWhatsApp) {
      const text = encodeURIComponent(
        `Hi Dr. ${(doctor?.name || '').replace(/^Dr\.?\s+/i, '')}, I just saved your contact and would like to book an appointment.`
      );
      return `https://wa.me/91${phoneE164}?text=${text}`;
    }
    return landingUrl;
  }, [canWhatsApp, doctor?.name, phoneE164, landingUrl]);

  const vcardText = useMemo(
    () => buildVcard(doctor, landingUrl, { includePhone: !!doctor?.qr_share_phone }),
    [doctor, landingUrl]
  );

  useEffect(() => {
    if (!isEnabled || !open) return;
    (async () => {
      try {
        const [v, m] = await Promise.all([
          QRCode.toDataURL(vcardText, { width: 600, margin: 2, errorCorrectionLevel: 'Q', color: { dark: '#0f172a', light: '#ffffff' } }),
          QRCode.toDataURL(messageQrUrl, { width: 600, margin: 2, errorCorrectionLevel: 'Q', color: { dark: '#065f46', light: '#ffffff' } }),
        ]);
        setVcardQr(v); setMessageQr(m);
      } catch { /* silent */ }
    })();
  }, [isEnabled, open, vcardText, messageQrUrl]);

  const trackOpen = () => {
    if (!slug) return;
    api.post('/track/event', { doctor_slug: slug, event_type: 'page_view', source: 'qr-widget', referrer: 'save-my-details-widget' }).catch(() => {});
  };

  if (!isEnabled) return null;

  return (
    <>
      {/* Floating pill */}
      <button
        onClick={() => { setOpen(true); trackOpen(); }}
        className="hidden md:flex fixed bottom-24 right-6 z-40 items-center gap-2 px-4 py-3 rounded-full bg-slate-900 text-white text-sm font-medium shadow-xl hover:bg-slate-800 transition-all hover:scale-105"
        data-testid="save-my-details-pill"
        aria-label="Save my details"
      >
        <QrCode size={16} className="text-amber-300" />
        Save my details
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="save-my-details-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Scan · Save · Connect</p>
                <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Save {doctor.name}'s details</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-save-modal"><X size={18} /></button>
            </div>

            {/* Body — dual QR */}
            <div className="grid grid-cols-2 gap-4 p-6">
              <div className="text-center">
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 mb-3">
                  {vcardQr ? (
                    <img src={vcardQr} alt="Contact QR" className="w-full" data-testid="widget-vcard-qr" />
                  ) : <div className="aspect-square animate-pulse bg-slate-100 rounded-lg" />}
                </div>
                <p className="text-xs font-semibold text-slate-900 flex items-center justify-center gap-1">
                  <Phone size={11} className="text-emerald-500" /> Save contact
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {doctor?.qr_share_phone ? 'Saves phone + clinic in one tap' : 'Saves name + clinic — phone stays private'}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 mb-3">
                  {messageQr ? (
                    <img src={messageQr} alt="Message me QR" className="w-full" data-testid="widget-message-qr" />
                  ) : <div className="aspect-square animate-pulse bg-slate-100 rounded-lg" />}
                </div>
                <p className="text-xs font-semibold text-slate-900 flex items-center justify-center gap-1">
                  <MessageCircle size={11} className="text-emerald-500" /> {canWhatsApp ? 'Message on WhatsApp' : 'Message me now'}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {canWhatsApp ? 'Opens WhatsApp with a ready message' : 'Secure in-app chat — the doctor will know you enquired'}
                </p>
              </div>
            </div>

            {/* Tip */}
            <div className="mx-6 mb-4 bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-start gap-2">
              <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-emerald-700 leading-relaxed">
                Open your phone's camera and point it at a QR above — tap the notification to save or open instantly.
              </p>
            </div>

            {/* Branded footer */}
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center">
              <p className="text-[10px] text-slate-400">Powered by DocSite Platform&trade;</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
